import { useState, useEffect, useCallback, useMemo } from 'react';
import { createCustomerETLApi } from '../api/etlServiceApi';

// ── Helpers ──────────────────────────────────────────────────────────────────

const TABLES_SQL =
  "SELECT TABLE_SCHEMA, TABLE_NAME, TABLE_TYPE FROM INFORMATION_SCHEMA.TABLES " +
  "WHERE TABLE_TYPE IN ('BASE TABLE', 'VIEW') ORDER BY TABLE_TYPE, TABLE_SCHEMA, TABLE_NAME";

function statusColor(status) {
  if (status === 'ok') return 'bg-emerald-500';
  if (status === 'error') return 'bg-red-500';
  if (status === 'running') return 'bg-amber-400 animate-pulse';
  return 'bg-gray-300';
}

function statusText(status) {
  if (status === 'ok') return 'text-emerald-700';
  if (status === 'error') return 'text-red-600';
  if (status === 'running') return 'text-amber-600';
  return 'text-gray-400';
}

// ── Step header pill ─────────────────────────────────────────────────────────

function StepBadge({ num, label, status, active }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors
      ${active ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-200' : 'bg-gray-50 text-gray-400'}`}>
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusColor(status)}`} />
      <span>{num}. {label}</span>
    </div>
  );
}

// ── Step card ────────────────────────────────────────────────────────────────

function StepCard({ num, title, children, status }) {
  return (
    <div className={`rounded-xl border p-4 transition-colors
      ${status === 'ok' ? 'border-emerald-200 bg-emerald-50/40'
        : status === 'error' ? 'border-red-200 bg-red-50/40'
        : 'border-gray-200 bg-white'}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0
          ${status === 'ok' ? 'bg-emerald-500' : status === 'error' ? 'bg-red-500' : status === 'running' ? 'bg-amber-400' : 'bg-gray-300'}`}>
          {status === 'ok' ? '✓' : status === 'error' ? '✕' : num}
        </span>
        <span className={`text-sm font-semibold ${statusText(status)}`}>{title}</span>
        {status === 'running' && (
          <span className="ml-auto w-3.5 h-3.5 border-2 border-amber-300 border-t-amber-500 rounded-full animate-spin" />
        )}
      </div>
      {children}
    </div>
  );
}

// ── Results table ─────────────────────────────────────────────────────────────

function ResultsTable({ columns, rows, rowCount, executionTimeMs }) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden mt-3">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
        <span className="text-xs font-medium text-gray-600">
          {rowCount.toLocaleString()} row{rowCount !== 1 ? 's' : ''}
          {columns.length > 0 && ` · ${columns.length} col${columns.length !== 1 ? 's' : ''}`}
        </span>
        {executionTimeMs != null && (
          <span className="text-xs text-gray-400">{executionTimeMs.toFixed(1)} ms</span>
        )}
      </div>
      <div className="overflow-auto max-h-[400px]">
        <table className="text-xs min-w-full">
          <thead className="sticky top-0 bg-gray-50">
            <tr>
              {columns.map(col => (
                <th key={col} className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap border-b border-gray-200">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50">
                {columns.map(col => (
                  <td
                    key={col}
                    className="px-3 py-1.5 text-gray-700 whitespace-nowrap font-mono max-w-[260px] truncate"
                    title={String(row[col] ?? '')}
                  >
                    {row[col] == null
                      ? <span className="text-gray-300 italic">null</span>
                      : String(row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function DBExplorer({ customerId, etlServiceReachable }) {
  const api = useMemo(() => (customerId ? createCustomerETLApi(customerId) : null), [customerId]);
  // Step 1 — VPN
  const [vpn, setVpn] = useState({ status: 'pending', label: null });
  // Step 2 — DB
  const [db, setDb] = useState({ status: 'pending', label: null, error: null });
  // Step 3 — Tables
  const [tables, setTables] = useState({ status: 'pending', rows: [], selected: null });
  // Step 4 — Query
  const [sql, setSql] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [queryRunning, setQueryRunning] = useState(false);
  const [queryError, setQueryError] = useState(null);

  // ── Steps 1 + 2: run on mount ───────────────────────────────────────────────

  const runConnectionChecks = useCallback(async () => {
    if (!etlServiceReachable || !api) return;

    // Step 1 — VPN
    setVpn({ status: 'running', label: null });
    setDb({ status: 'pending', label: null, error: null });
    setTables({ status: 'pending', rows: [], selected: null });
    setQueryResult(null);
    setQueryError(null);

    let vpnOk = false;
    try {
      const health = await api.getHealth();
      const v = health.vpn_status;
      vpnOk = v === 'connected' || v === 'not_required';
      setVpn({
        status: vpnOk ? 'ok' : 'error',
        label: v === 'not_required' ? 'Not required (local mode)' : `Status: ${v}`,
      });
    } catch (e) {
      setVpn({ status: 'error', label: 'ETL service not reachable on port 8001 — etl-service-vpn may not be running.' });
      return;
    }

    // Step 2 — DB (run regardless of VPN — graceful degradation is fine)
    setDb({ status: 'running', label: null, error: null });
    try {
      const status = await api.getStatus();
      const ok = status.db_status === 'connected';
      setDb({
        status: ok ? 'ok' : 'error',
        label: ok
          ? `Connected to ${status.db_name} on ${status.db_host}`
          : `${status.db_status}${status.db_host ? ` (${status.db_host})` : ''}`,
        error: ok ? null : status.db_error,
      });
    } catch (e) {
      setDb({ status: 'error', label: 'Status request failed', error: e.message });
    }
  }, [etlServiceReachable, api]);

  useEffect(() => {
    runConnectionChecks();
  }, [runConnectionChecks]);

  // ── Step 3 — Discover tables ────────────────────────────────────────────────

  const discoverTables = useCallback(async () => {
    if (!api) return;
    setTables({ status: 'running', rows: [], selected: null });
    setQueryResult(null);
    setQueryError(null);
    setSql('');
    try {
      const res = await api.executeERPQuery(TABLES_SQL);
      if (!res.success) {
        setTables({ status: 'error', rows: [], selected: null, error: res.error });
        return;
      }
      setTables({ status: 'ok', rows: res.rows, selected: null });
    } catch (e) {
      setTables({ status: 'error', rows: [], selected: null, error: e?.response?.data?.detail ?? e.message });
    }
  }, [api]);

  // ── Step 4 — Select table → pre-fill SQL ────────────────────────────────────

  const selectTable = useCallback((row) => {
    const fullName = row.TABLE_SCHEMA && row.TABLE_SCHEMA !== 'dbo'
      ? `[${row.TABLE_SCHEMA}].[${row.TABLE_NAME}]`
      : `[${row.TABLE_NAME}]`;
    setTables(prev => ({ ...prev, selected: row.TABLE_NAME }));
    setSql(`SELECT TOP 100 *\nFROM ${fullName}`);
    setQueryResult(null);
    setQueryError(null);
  }, []);

  // ── Step 4 — Run query ──────────────────────────────────────────────────────

  const runQuery = useCallback(async () => {
    if (!sql.trim()) return;
    setQueryRunning(true);
    setQueryError(null);
    setQueryResult(null);
    try {
      const res = await api.executeERPQuery(sql);
      if (res.success) {
        setQueryResult(res);
      } else {
        setQueryError(res.error ?? 'Query failed');
      }
    } catch (e) {
      setQueryError(e?.response?.data?.detail ?? e.message ?? 'Request failed');
    } finally {
      setQueryRunning(false);
    }
  }, [sql, api]);

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      runQuery();
    }
  };

  const dbOk = db.status === 'ok';
  const activeStep = vpn.status === 'pending' || vpn.status === 'running' ? 1
    : db.status === 'pending' || db.status === 'running' ? 2
    : tables.status === 'pending' || tables.status === 'running' ? 3
    : 4;

  return (
    <div className="space-y-4">

      {/* Step progress pills */}
      <div className="flex items-center gap-2 flex-wrap">
        <StepBadge num={1} label="VPN" status={vpn.status} active={activeStep === 1} />
        <svg className="w-3 h-3 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        <StepBadge num={2} label="Database" status={db.status} active={activeStep === 2} />
        <svg className="w-3 h-3 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        <StepBadge num={3} label="Tables" status={tables.status} active={activeStep === 3} />
        <svg className="w-3 h-3 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        <StepBadge num={4} label="SQL Query" status={queryResult ? 'ok' : queryError ? 'error' : 'pending'} active={activeStep === 4} />

        <button
          onClick={runConnectionChecks}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
          Re-check
        </button>
      </div>

      {/* Step 1 — VPN */}
      <StepCard num={1} title="VPN Connection" status={vpn.status}>
        {vpn.status === 'pending' && <p className="text-xs text-gray-400">Waiting…</p>}
        {vpn.status === 'running' && <p className="text-xs text-amber-600">Checking VPN status…</p>}
        {vpn.status === 'ok' && <p className="text-xs text-emerald-700">{vpn.label ?? 'Connected'}</p>}
        {vpn.status === 'error' && (
          <div className="space-y-1.5">
            <p className="text-xs text-red-600">{vpn.label ?? 'VPN error'}</p>
            <div className="text-[11px] text-gray-400 space-y-0.5 pt-1 border-t border-red-100">
              <p>Make sure both containers are running:</p>
              <code className="block bg-gray-100 text-gray-600 px-2 py-1 rounded font-mono">
                docker compose -f etl-service/docker-compose.vpn.yml up -d
              </code>
              <p className="pt-0.5">Then verify: <span className="font-mono">etl-vpn</span> and <span className="font-mono">etl-service-vpn</span> should both show <span className="font-mono">Up</span>.</p>
            </div>
          </div>
        )}
      </StepCard>

      {/* Step 2 — Database */}
      <StepCard num={2} title="Database Connection" status={db.status}>
        {db.status === 'pending' && <p className="text-xs text-gray-400">Waiting for VPN check…</p>}
        {db.status === 'running' && <p className="text-xs text-amber-600">Connecting to SQL Server…</p>}
        {db.status === 'ok' && <p className="text-xs text-emerald-700">{db.label}</p>}
        {db.status === 'error' && (
          <div className="space-y-1">
            <p className="text-xs text-red-600">{db.label}</p>
            {db.error && <p className="text-xs text-red-400 font-mono">{db.error}</p>}
          </div>
        )}
      </StepCard>

      {/* Step 3 — Table discovery */}
      <StepCard num={3} title="Table Discovery" status={tables.status}>
        {tables.status === 'pending' && (
          <button
            onClick={discoverTables}
            disabled={!dbOk}
            className="flex items-center gap-2 px-4 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-400 text-white text-xs font-medium rounded-lg transition-colors"
          >
            Discover Tables
          </button>
        )}
        {tables.status === 'running' && <p className="text-xs text-amber-600">Querying INFORMATION_SCHEMA…</p>}
        {tables.status === 'error' && (
          <div className="space-y-2">
            <p className="text-xs text-red-600 font-mono">{tables.error}</p>
            <button onClick={discoverTables} className="text-xs text-primary-600 hover:underline">Retry</button>
          </div>
        )}
        {tables.status === 'ok' && (
          <div className="space-y-2">
            <p className="text-xs text-emerald-700">{tables.rows.length} object{tables.rows.length !== 1 ? 's' : ''} found — click one to query it</p>
            <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {tables.rows.map((row, i) => {
                const name = row.TABLE_NAME;
                const schema = row.TABLE_SCHEMA;
                const isView = row.TABLE_TYPE === 'VIEW';
                const isSelected = tables.selected === name;
                return (
                  <button
                    key={i}
                    onClick={() => selectTable(row)}
                    className={`w-full text-left px-3 py-2 text-xs font-mono transition-colors flex items-center gap-2
                      ${isSelected
                        ? 'bg-primary-50 text-primary-700 font-semibold'
                        : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />}
                    <span className="text-gray-400">{schema}.</span>{name}
                    {isView && (
                      <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-violet-50 border border-violet-200 text-violet-600 font-sans font-medium flex-shrink-0">
                        view
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <button onClick={discoverTables} className="text-xs text-gray-400 hover:text-gray-600 hover:underline">Refresh</button>
          </div>
        )}
      </StepCard>

      {/* Step 4 — SQL query */}
      <StepCard num={4} title="SQL Query" status={queryResult ? 'ok' : queryError ? 'error' : 'pending'}>
        {!tables.selected && tables.status !== 'ok' && (
          <p className="text-xs text-gray-400">Select a table in step 3 to pre-fill a query, or type your own.</p>
        )}
        {(tables.selected || tables.status === 'ok') && (
          <div className="space-y-3">
            {tables.selected && (
              <p className="text-xs text-gray-500">
                Querying <span className="font-mono font-semibold text-gray-700">{tables.selected}</span> — edit below as needed.
              </p>
            )}
            <div className="relative">
              <textarea
                value={sql}
                onChange={e => setSql(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={6}
                spellCheck={false}
                placeholder="SELECT TOP 100 * FROM [TableName]"
                className="w-full font-mono text-sm border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y bg-gray-50 text-gray-800"
              />
              <div className="absolute bottom-2 right-3 text-xs text-gray-300 pointer-events-none select-none">
                Ctrl+Enter to run
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={runQuery}
                disabled={queryRunning || !sql.trim() || !dbOk}
                className="flex items-center gap-2 px-5 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {queryRunning ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Running…
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
                    </svg>
                    Run Query
                  </>
                )}
              </button>
              {!dbOk && <span className="text-xs text-amber-600">DB not connected</span>}
            </div>

            {queryError && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <p className="text-xs font-semibold text-red-700 mb-1">Query Error</p>
                <p className="text-xs text-red-600 font-mono whitespace-pre-wrap">{queryError}</p>
              </div>
            )}

            {queryResult && queryResult.columns.length > 0 && (
              <ResultsTable
                columns={queryResult.columns}
                rows={queryResult.rows}
                rowCount={queryResult.row_count}
                executionTimeMs={queryResult.execution_time_ms}
              />
            )}
            {queryResult && queryResult.columns.length === 0 && (
              <div className="text-center py-6 text-sm text-gray-400">Query returned 0 rows.</div>
            )}
          </div>
        )}
      </StepCard>
    </div>
  );
}
